import { useCallback, useEffect, useState } from 'react';
import { FilmIcon, Loader2Icon, Trash2Icon, UploadIcon, VideoIcon } from 'lucide-react';
import { ApiError } from '../../api/client.js';
import {
  createWebinar,
  deleteWebinar,
  listWebinars,
  mintWebinarUploadUrl,
  putWithProgress,
  type WebinarListItem,
} from './AdminElearningApi.js';

const VIDEO_TYPES = ['video/mp4', 'video/webm'];
const MAX_VIDEO_GB = 2;

/** "webinar_slaap-2026.mp4" → "webinar slaap 2026": een bruikbare starttitel. */
function titelUitBestandsnaam(name: string): string {
  return name
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .slice(0, 200);
}

/**
 * Beheer → E-learning → Webinar-opnames. Bewust één stap: bestand kiezen of
 * slepen start de upload meteen, de titel staat al ingevuld en terwijl de
 * upload loopt kan de admin titel en datum aanpassen. Opslaan zet de opname
 * direct op de Scholing-pagina.
 */
export function AdminWebinarUploads(): JSX.Element {
  const [webinars, setWebinars] = useState<WebinarListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [videoPath, setVideoPath] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setWebinars((await listWebinars()).webinars);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Webinars konden niet geladen worden.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const busy = uploadPct !== null && videoPath === null;

  async function handleFile(file: File): Promise<void> {
    setError(null);
    if (!VIDEO_TYPES.includes(file.type)) {
      setError('Alleen MP4 (H.264) of WebM wordt ondersteund.');
      return;
    }
    if (file.size > MAX_VIDEO_GB * 1024 * 1024 * 1024) {
      setError(`Een opname mag maximaal ${MAX_VIDEO_GB} GB zijn.`);
      return;
    }
    setTitle(titelUitBestandsnaam(file.name));
    setVideoPath(null);
    setUploadPct(0);
    try {
      const { uploadUrl, videoPath: path } = await mintWebinarUploadUrl({
        mimeType: file.type,
        sizeBytes: file.size,
      });
      await putWithProgress(uploadUrl, file, setUploadPct);
      setVideoPath(path);
    } catch (err) {
      setUploadPct(null);
      setError(err instanceof ApiError ? err.message : 'Uploaden mislukt. Probeer opnieuw.');
    }
  }

  function reset(): void {
    setTitle('');
    setDate('');
    setUploadPct(null);
    setVideoPath(null);
  }

  async function handleSave(): Promise<void> {
    if (!videoPath || !title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createWebinar({ title: title.trim(), date: date || null, videoPath });
      reset();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(w: WebinarListItem): Promise<void> {
    if (!window.confirm(`Weet je zeker dat je "${w.title}" wilt verwijderen?`)) return;
    setDeletingId(w.id);
    try {
      await deleteWebinar(w.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verwijderen mislukt.');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section className="mt-10">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-bold text-ink-900">
        <VideoIcon size={18} className="text-brand-700" /> Webinar-opnames
      </h2>
      <p className="mb-4 text-sm text-ink-700/70">
        Upload een opname en hij staat direct onder Scholing → Webinars om terug te kijken.
      </p>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {uploadPct === null ? (
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files[0];
            if (f) void handleFile(f);
          }}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
            dragOver
              ? 'border-brand-500 bg-brand-50'
              : 'border-brand-200 bg-white hover:bg-brand-50/50'
          }`}
        >
          <UploadIcon className="mb-2 h-7 w-7 text-brand-700" />
          <span className="text-sm font-semibold text-ink-900">
            Sleep een opname hierheen of klik om te kiezen
          </span>
          <span className="mt-1 text-xs text-ink-700/60">
            MP4 (H.264) of WebM, maximaal {MAX_VIDEO_GB} GB
          </span>
          <input
            type="file"
            accept="video/mp4,video/webm"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = '';
            }}
          />
        </label>
      ) : (
        <div className="rounded-xl border border-brand-100 bg-white p-4 shadow-card">
          <div className="mb-3 flex items-center justify-between text-xs text-ink-700/70">
            <span className="inline-flex items-center gap-1.5">
              {busy ? (
                <Loader2Icon size={13} className="animate-spin" />
              ) : (
                <FilmIcon size={13} className="text-brand-700" />
              )}
              {busy ? 'Bezig met uploaden…' : 'Upload klaar'}
            </span>
            <span>{uploadPct}%</span>
          </div>
          <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-brand-50">
            <div
              className="h-full bg-brand-700 transition-all"
              style={{ width: `${uploadPct}%` }}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <label className="text-xs font-semibold text-ink-800">
              Titel
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                className="mt-1 w-full rounded-md border border-brand-100 px-3 py-2 text-sm font-normal"
              />
            </label>
            <label className="text-xs font-semibold text-ink-800">
              Datum webinar (optioneel)
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-brand-100 px-3 py-2 text-sm font-normal"
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            {/* ponytail: annuleren laat het geüploade object als wees achter, net als bij lesvideo's */}
            <button
              type="button"
              onClick={reset}
              disabled={busy || saving}
              className="rounded-full px-4 py-2 text-sm text-ink-800 hover:bg-brand-50 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!videoPath || !title.trim() || saving}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {saving && <Loader2Icon size={14} className="animate-spin" />} Opslaan
            </button>
          </div>
        </div>
      )}

      {webinars.length > 0 && (
        <ul className="mt-4 divide-y divide-brand-50 rounded-xl border border-brand-100 bg-white shadow-card">
          {webinars.map((w) => (
            <li key={w.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
              <div className="min-w-0">
                <p className="truncate font-medium text-ink-900">{w.title}</p>
                <p className="text-xs text-ink-700/60">
                  {w.date
                    ? new Date(`${w.date}T12:00:00`).toLocaleDateString('nl-NL')
                    : `Geüpload ${new Date(w.createdAt).toLocaleDateString('nl-NL')}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleDelete(w)}
                disabled={deletingId === w.id}
                aria-label={`Verwijder ${w.title}`}
                className="inline-flex items-center justify-center rounded-md border border-red-100 bg-red-50 p-1.5 text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                {deletingId === w.id ? (
                  <Loader2Icon size={14} className="animate-spin" />
                ) : (
                  <Trash2Icon size={14} />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
